/**
 * Normalise hashtag symbols: a `%` used where a `#` belongs (`%snowboard` →
 * `#snowboard`). The backend fixes this inside CREATE_CONTENT_DRAFT, but when a
 * draft leaks as raw JSON text that tool never runs, so the `%` reaches the
 * frontend unfixed. Apply this on the draft body wherever it's parsed so display,
 * the editor, AND publish all use `#`. Mirrors yepairag's `_HASHTAG_PCT_RE`
 * (a `%` at line start or after whitespace, immediately before a word char).
 */
export function fixHashtagSymbols(text?: string | null): string {
  return text ? text.replace(/(^|\s)%(?=\w)/gm, '$1#') : '';
}

/**
 * A tag rendered as a chip, with exactly one leading `#`.
 *
 * The agent sends tags both ways — `hydrogen` and `#hydrogen` — and every chip in
 * the app prepended `#` unconditionally, so a tag that already carried one rendered
 * as `##hydrogen` (seen on a Draft-ready card, 2026-08-21). Strips any run of
 * leading hashes and surrounding whitespace, then puts back exactly one. Returns ''
 * for a tag that is empty or nothing but hashes, so the caller can drop it.
 */
export function hashtagLabel(tag?: string | null): string {
  const bare = (tag ?? '').trim().replace(/^#+/, '').trim();
  return bare ? `#${bare}` : '';
}

/**
 * Turn a draft body (HTML or markdown) into plain, human-readable text for a
 * card PREVIEW — so `<h2>`, `<p>`, markdown markers, etc. never show up as raw
 * characters. The full rich body is still edited/published via the editor; this
 * is only for the collapsed preview line.
 */
export function htmlToText(body?: string | null, opts?: { preserveBreaks?: boolean }): string {
  if (!body) return '';
  const preserveBreaks = opts?.preserveBreaks ?? false;
  let text = body;
  // Markdown images are shown separately as the hero — drop them from the text.
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ');
  // Keep paragraph/line structure for a full read view (collapse it for a preview).
  if (preserveBreaks) {
    text = text
      .replace(/<\/(p|h[1-6]|li|div|blockquote|tr)>/gi, '\n\n')
      .replace(/<br\s*\/?>/gi, '\n');
  }
  // HTML → text via the DOM (decodes entities, drops tags) when available.
  if (/<[a-z!/][\s\S]*>/i.test(text) && typeof document !== 'undefined') {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    text = doc.body.textContent || '';
  } else {
    text = text.replace(/<[^>]+>/g, ' ');
  }
  // Remaining markdown: links → their label, then strip heading/emphasis markers.
  text = text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    // 行首 marker 连串照旧剥掉,但 hashtag 不是 marker:CommonMark 的标题记号
    // 要求 # 后跟空格,`#usedoutdoorgear` 是行首的话题标签——老正则把它的 #
    // 吃掉,IG 文案里换行后的第一个标签就裂成裸词(2026-08-26 实修,`- #tag`
    // 同理)。marker 串收尾的 # 若紧跟文字,原样保留。
    .replace(/^[#>\-*\s]+/gm, (run: string, offset: number, s: string) => {
      const next = s[offset + run.length] ?? '';
      const trailingHashes = run.match(/#+$/);
      if (!trailingHashes || !/\w/.test(next)) {
        // [bug·Monica 2026-08-28] marker 串把它前面的换行也吞了,原来一律换成一个空格 ——
        // 于是 preserveBreaks 模式下「空行 + ## 小标题」整段塌成一行,博客预览挤成一大坨。
        // 保留段落时把吞进来的换行还回去(不保留段落的老行为不变:仍旧折成一个空格)。
        const newlines = run.replace(/[^\n]/g, '');
        return preserveBreaks && newlines ? newlines : ' ';
      }
      // 保留 hashtag 时,run 里吞进来的换行也要还回去,否则标签行前的空行塌掉。
      const newlines = run.slice(0, run.length - trailingHashes[0].length).replace(/[^\n]/g, '');
      return newlines + trailingHashes[0];
    })
    .replace(/[*_`~]+/g, '');
  if (preserveBreaks) {
    // Collapse runs of spaces/tabs but keep paragraph breaks; cap blank lines.
    return text
      .replace(/[ \t]+/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]*\n[ \t]*/g, '\n')
      .trim();
  }
  return text.replace(/\s+/g, ' ').trim();
}
